import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, HeartPulse, Activity, Megaphone, Send } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function MeSafety() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("action") === "incident" ? "incident" : "incident";
  return (
    <div className="space-y-4" data-testid="me-safety-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500">Health &amp; Safety</div>
        <h1 className="text-3xl font-bold font-display mt-1">Safety &amp; Wellbeing</h1>
        <p className="text-sm text-neutral-500 mt-1">Report incidents, take the ergonomic check, submit the weekly pulse, and read the latest toolbox talks.</p>
      </div>
      <Tabs defaultValue={initialTab}>
        <TabsList className="bg-neutral-100 flex-wrap h-auto">
          <TabsTrigger value="incident" data-testid="ms-tab-incident"><AlertTriangle className="w-4 h-4 mr-1" /> Report incident</TabsTrigger>
          <TabsTrigger value="ergonomic" data-testid="ms-tab-ergonomic"><Activity className="w-4 h-4 mr-1" /> Ergonomic check</TabsTrigger>
          <TabsTrigger value="pulse" data-testid="ms-tab-pulse"><HeartPulse className="w-4 h-4 mr-1" /> Wellbeing pulse</TabsTrigger>
          <TabsTrigger value="toolbox" data-testid="ms-tab-toolbox"><Megaphone className="w-4 h-4 mr-1" /> Toolbox talks</TabsTrigger>
        </TabsList>
        <TabsContent value="incident" className="mt-6"><IncidentForm /></TabsContent>
        <TabsContent value="ergonomic" className="mt-6"><ErgonomicQuiz /></TabsContent>
        <TabsContent value="pulse" className="mt-6"><WellbeingPulse /></TabsContent>
        <TabsContent value="toolbox" className="mt-6"><ToolboxList /></TabsContent>
      </Tabs>
    </div>
  );
}

function IncidentForm() {
  const [form, setForm] = useState({ kind:"near-miss", severity:"low", description:"", location:"" });
  const submit = async () => {
    if (!form.description.trim()) return toast.error("Describe what happened");
    await api.post("/compliance-plus/incidents", form);
    toast.success("Incident reported — HR has been notified");
    setForm({ kind:"near-miss", severity:"low", description:"", location:"" });
  };
  return (
    <Card className="border-0 shadow-sm" data-testid="incident-form">
      <CardContent className="p-6 space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Kind</Label>
            <select value={form.kind} onChange={e=>setForm({...form, kind:e.target.value})} className="w-full px-3 py-2 text-sm rounded-lg border border-neutral-200" data-testid="incident-kind">
              <option value="near-miss">Near-miss</option>
              <option value="injury">Injury</option>
              <option value="property-damage">Property damage</option>
              <option value="psychological">Psychological / bullying</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Severity</Label>
            <select value={form.severity} onChange={e=>setForm({...form, severity:e.target.value})} className="w-full px-3 py-2 text-sm rounded-lg border border-neutral-200" data-testid="incident-severity">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>
        <div><Label className="text-xs">Location (optional)</Label><Input value={form.location} onChange={e=>setForm({...form, location:e.target.value})} placeholder="Office kitchen, home office, client site…" /></div>
        <div><Label className="text-xs">Describe what happened</Label><Textarea rows={5} value={form.description} onChange={e=>setForm({...form, description:e.target.value})} placeholder="What happened, who was involved, and any immediate actions taken." data-testid="incident-description" /></div>
        <Button onClick={submit} className="btn-primary rounded-full" data-testid="incident-submit"><Send className="w-3.5 h-3.5 mr-1" /> Report incident</Button>
      </CardContent>
    </Card>
  );
}

function ErgonomicQuiz() {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/compliance-plus/ergonomic-quiz").then(r=>{setData(r.data); if (r.data.prior) setResult(r.data.prior);}).catch(()=>{}); }, []);
  const submit = async () => {
    if (!data) return;
    const { data: r } = await api.post("/compliance-plus/ergonomic-quiz", { answers });
    setResult(r); toast.success("Ergonomic check complete");
  };
  if (!data) return <div>Loading…</div>;
  return (
    <Card className="border-0 shadow-sm" data-testid="ergonomic-quiz">
      <CardContent className="p-6 space-y-3">
        <div className="text-xs text-neutral-500">Annual · {data.year}</div>
        {result && (
          <div className={`p-3 rounded-lg ${result.risk_level==="low"?"bg-teal/10":result.risk_level==="medium"?"bg-pink/10":"bg-red-50"}`} data-testid="ergonomic-result">
            <div className="text-sm font-semibold">Score: {result.score}/100 — <span className="capitalize">{result.risk_level}</span> risk</div>
            <div className="text-xs text-neutral-600 mt-1">{result.risk_level==="low"?"Your setup looks great.":result.risk_level==="medium"?"A few improvements would help.":"Please contact HR — your setup needs attention."}</div>
          </div>
        )}
        <div className="space-y-2">
          {data.questions.map(q => (
            <div key={q.id} className="flex items-center justify-between p-3 rounded-lg bg-neutral-50" data-testid={`erg-q-${q.id}`}>
              <span className="text-sm flex-1">{q.prompt}</span>
              <div className="flex gap-1 shrink-0">
                <button onClick={()=>setAnswers({...answers, [q.id]: true})} className={`px-3 py-1 text-xs rounded-full border ${answers[q.id]===true?"bg-teal text-white border-teal":"hover:border-teal"}`} data-testid={`erg-yes-${q.id}`}>Yes</button>
                <button onClick={()=>setAnswers({...answers, [q.id]: false})} className={`px-3 py-1 text-xs rounded-full border ${answers[q.id]===false?"bg-neutral-800 text-white border-neutral-800":"hover:border-neutral-800"}`} data-testid={`erg-no-${q.id}`}>No</button>
              </div>
            </div>
          ))}
        </div>
        <Button onClick={submit} className="btn-primary rounded-full" data-testid="erg-submit">Submit assessment</Button>
      </CardContent>
    </Card>
  );
}

function WellbeingPulse() {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [mine, setMine] = useState([]);
  useEffect(() => { api.get("/compliance-plus/wellbeing-pulse/mine").then(r=>setMine(r.data)).catch(()=>setMine([])); }, []);
  const submit = async () => {
    if (!score) return toast.error("Pick a score first");
    await api.post("/compliance-plus/wellbeing-pulse", { score, comment });
    toast.success("Thanks — your response is anonymised in HR reports");
    api.get("/compliance-plus/wellbeing-pulse/mine").then(r=>setMine(r.data));
    setComment("");
  };
  return (
    <Card className="border-0 shadow-sm" data-testid="wellbeing-form">
      <CardContent className="p-6 space-y-4">
        <div>
          <div className="text-sm font-semibold">How are you doing this week?</div>
          <div className="text-xs text-neutral-500 mt-1">Anonymous in reports · used to spot burnout trends early</div>
        </div>
        <div className="flex gap-2 flex-wrap justify-center">
          {[1,2,3,4,5,6,7,8,9,10].map(n => (
            <button key={n} onClick={()=>setScore(n)}
              data-testid={`wb-score-${n}`}
              className={`w-10 h-10 rounded-lg border font-bold transition-all ${score===n?"bg-pink text-white border-pink":n<=3?"bg-red-50 border-red-200 text-red-500":n<=6?"bg-amber-50 border-amber-200 text-amber-700":"bg-teal/5 border-teal/20 text-teal hover:bg-teal/10"}`}>
              {n}
            </button>
          ))}
        </div>
        <Textarea rows={2} value={comment} onChange={e=>setComment(e.target.value)} placeholder="Anything on your mind? (optional)" data-testid="wb-comment" />
        <Button onClick={submit} className="btn-primary rounded-full" data-testid="wb-submit">Send this week's pulse</Button>
        {mine.length > 0 && (
          <div className="pt-4 border-t">
            <div className="text-xs text-neutral-500 mb-2">Your recent scores</div>
            <div className="flex gap-1 flex-wrap">
              {mine.slice(0, 12).map(m => (
                <div key={m.pulse_id} title={new Date(m.created_at).toLocaleDateString()} className={`w-8 h-8 rounded font-mono text-xs grid place-items-center ${m.score<=3?"bg-red-100 text-red-700":m.score<=6?"bg-amber-100 text-amber-700":"bg-teal/10 text-teal"}`}>{m.score}</div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ToolboxList() {
  const [talks, setTalks] = useState([]);
  const [acked, setAcked] = useState(new Set());
  useEffect(() => { api.get("/compliance-plus/toolbox-talks").then(r=>setTalks(r.data)).catch(()=>setTalks([])); }, []);
  const ack = async (talk_id) => {
    await api.post(`/compliance-plus/toolbox-talks/${talk_id}/acknowledge`);
    setAcked(new Set([...acked, talk_id]));
    toast.success("Acknowledged");
  };
  return (
    <div className="space-y-3" data-testid="toolbox-list">
      {talks.map(t => (
        <Card key={t.talk_id} className="border-0 shadow-sm" data-testid={`me-talk-${t.talk_id}`}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold">{t.title}</h3>
                <p className="text-sm text-neutral-600 mt-2">{t.content}</p>
                <div className="text-[11px] text-neutral-500 mt-2">{t.duration_min} min read</div>
              </div>
              {acked.has(t.talk_id) ? (
                <Badge className="bg-teal text-white border-0 shrink-0">Acked</Badge>
              ) : (
                <Button size="sm" onClick={()=>ack(t.talk_id)} data-testid={`me-talk-ack-${t.talk_id}`}>I've read this</Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
